import { TEMPLATE_DIMENSIONS_BY_ASPECT_RATIO } from 'constants/templates';
import {
  createCanvaDesign as createCanvaDesignAction,
  createCanvaExport,
  getCanvaExport,
} from 'redux/middleware/api/third-party-authentication-service/actions';
import {
  CanvaExportResult,
  CreateCanvaDesignResult,
} from 'redux/middleware/api/third-party-authentication-service/types';
import { showError } from 'redux/modules/notification';
import { ThunkAction } from 'redux/types';
import { AspectRatioName } from 'types';
import reduxPoll from 'utils/redux-poll';
import { Type } from './action-types';
import {
  canvaProviderUserIdSelector,
  canvaUserDataSelector,
} from './selectors';
import { CanvaAuthorizeSuccessAction, CanvaPostMessageData } from './types';

const CANVA_EXPORT_POLL_ID = 'app/canva/export-poll';

export const canvaAuthorizeSuccess = (
  data: Pick<
    CanvaPostMessageData['tokenInfo'],
    'accessToken' | 'idToken' | 'providerUserId'
  >,
): CanvaAuthorizeSuccessAction => ({
  type: Type.CANVA_AUTHORIZE_SUCCESS,
  payload: data,
});

export const clearCanvaUserAction = { type: Type.CANVA_USER_CLEAR };

export const createCanvaDesign = (
  aspectRatioName: AspectRatioName,
): ThunkAction<Promise<CreateCanvaDesignResult>> => async (
  dispatch,
  getState,
) => {
  dispatch({ type: Type.CREATE_CANVA_DESIGN_REQUEST });

  const { width, height } = TEMPLATE_DIMENSIONS_BY_ASPECT_RATIO[
    aspectRatioName
  ];

  const providerUserId = canvaProviderUserIdSelector(getState());

  try {
    const { response } = await dispatch(
      createCanvaDesignAction({
        providerUserId,
        title: `Headliner Asset ${new Date(Date.now()).toISOString()}`,
        width,
        height,
      }),
    );

    dispatch({ type: Type.CREATE_CANVA_DESIGN_SUCCESS });

    return response;
  } catch (error) {
    dispatch(showError({ message: 'Error creating canva design' }));
    dispatch({ type: Type.CREATE_CANVA_DESIGN_FAILURE });
  }

  return undefined;
};

export const clearCanvaDesignInProgressAction = {
  type: Type.CLEAR_CANVA_DESIGN_IN_PROGRESS,
};

const waitForCanvaExport = (
  exportId: string,
): ThunkAction<Promise<CanvaExportResult>> => async (dispatch, getState) => {
  const { providerUserId, accessToken } =
    canvaUserDataSelector(getState()) ?? {};

  try {
    return await reduxPoll(
      dispatch,
      async () => {
        const { response } = await dispatch(
          getCanvaExport({
            exportId,
            providerUserId,
            accessToken,
          }),
        );

        return response;
      },
      {
        id: CANVA_EXPORT_POLL_ID,
        intervalMillis: 1500,
        shouldContinue: (error, response) => {
          if (error || response?.status === 'failed') {
            throw error;
          }

          if (response?.status === 'completed' && response?.imageUrl) {
            return false;
          }

          return true;
        },
      },
    );
  } catch {
    throw new Error('Error exporting canva design');
  }
};

export const exportCanvaDesign = (
  designId: string,
): ThunkAction<Promise<CanvaExportResult>> => async (dispatch, getState) => {
  dispatch({ type: Type.EXPORT_CANVA_DESIGN_REQUEST });

  const { providerUserId, accessToken } =
    canvaUserDataSelector(getState()) ?? {};

  try {
    const { response } = await dispatch(
      createCanvaExport({
        designId,
        providerUserId,
        accessToken,
      }),
    );

    const canvaExportResult = await dispatch(waitForCanvaExport(response.id));

    dispatch({ type: Type.EXPORT_CANVA_DESIGN_SUCCESS });

    return canvaExportResult;
  } catch {
    dispatch({ type: Type.EXPORT_CANVA_DESIGN_FAILURE });

    throw new Error('Error exporting canva design');
  }
};
